import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { Close, KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons'
import { Spacings, Text, Devices } from '@styles'
import { debounce } from '@utils'
import { FormElementContainer, Label, ErrorMessage } from '../styled'
import Checkbox from './Checkbox'
import SearchInput from './SearchInput'

const MultiSelectContainer = styled(FormElementContainer)`
	position: relative;
`

const FieldBox = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_1B};
	width: 100%;
	min-height: ${Spacings.SPACING_10B};
	box-sizing: border-box;
	padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_3B};
	font-size: ${Text.SMALL};
	border: 1px solid ${({ theme, $hasError }) => ($hasError ? theme.colors.status.error.border : theme.colors.border.default)};
	border-radius: ${({ theme }) => theme.radius.sm};
	background: ${({ theme, $disabled }) => ($disabled ? theme.colors.surface.sunken : theme.colors.surface.card)};
	cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
	position: relative;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;

	@media ${Devices.tablet} {
		font-size: ${Text.MEDIUM};
	}

	${({ $small }) =>
		$small &&
		css`
			min-height: ${Spacings.SPACING_8B};
			font-size: ${Text.SMALL};
		`}

	${({ $isOpen, theme, $hasError }) =>
		$isOpen &&
		css`
			border-color: ${$hasError ? theme.colors.status.error.border : theme.colors.border.focus};
			box-shadow: 0 0 0 3px ${$hasError ? theme.colors.status.error.bg : theme.colors.brand.subtle};
		`}
`

const PlaceholderText = styled.span`
	color: ${({ theme }) => theme.colors.text.secondary};
	font-weight: ${Text.REGULAR};
	white-space: nowrap;
`

const ChipItem = styled.span`
	display: inline-flex;
	align-items: center;
	flex-shrink: 0;
	gap: ${Spacings.SPACING_1};
	max-width: 100%;
	background: ${({ theme }) => theme.colors.brand.subtle};
	color: ${({ theme }) => theme.colors.brand.primary};
	border-radius: ${({ theme }) => theme.radius.sm};
	padding: ${Spacings.SPACING_1} ${Spacings.SPACING_2B};
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_500};
`

const ChipLabel = styled.span`
	max-width: 10rem;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const ChipRemoveButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: none;
	background: none;
	padding: 0;
	margin: 0;
	line-height: 0;
	color: inherit;
	cursor: pointer;

	svg {
		font-size: ${Text.MEDIUM};
	}

	&:hover {
		color: ${({ theme }) => theme.colors.brand.hover};
	}
`

const ChipsRow = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: nowrap;
	flex: 1 1 auto;
	min-width: 0;
	gap: ${Spacings.SPACING_1B};
	overflow-x: auto;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`

const IconWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	color: ${({ theme }) => theme.colors.text.secondary};
	padding: ${Spacings.SPACING_1};
	border-radius: 50%;
	transition: all 0.2s ease-in-out;

	svg {
		font-size: ${Text.LARGE};
	}
`

const DropdownPanel = styled.div`
	position: absolute;
	top: calc(100% + ${Spacings.SPACING_1B});
	left: 0;
	width: 100%;
	z-index: 600;
	background: ${({ theme }) => theme.colors.surface.card};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
	box-shadow: ${({ theme }) => theme.shadows.md};
	box-sizing: border-box;
	overflow: hidden;
	display: ${({ $show }) => ($show ? 'block' : 'none')};
	opacity: ${({ $show }) => ($show ? 1 : 0)};
	visibility: ${({ $show }) => ($show ? 'visible' : 'hidden')};
	transform: translateY(${({ $show }) => ($show ? '0' : '-4px')});
	transition: opacity 0.15s ease-in-out, transform 0.15s ease-in-out, visibility 0.15s;
`

const SearchRow = styled.div`
	padding: ${Spacings.SPACING_2B};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

const OptionsScrollArea = styled.div`
	max-height: calc(9 * ${Spacings.SPACING_8B});
	overflow-y: auto;
	scrollbar-width: thin;

	&::-webkit-scrollbar {
		width: ${Spacings.SPACING_6};
	}
	&::-webkit-scrollbar-thumb {
		background-color: ${({ theme }) => theme.colors.border.default};
		border-radius: calc(${Spacings.SPACING_6} / 2);
	}
`

const OptionRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
	cursor: pointer;
	transition: background 0.15s ease-in-out;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
	}

	&:hover {
		background: ${({ theme }) => theme.colors.brand.subtle};
	}

	${({ $highlighted, theme }) =>
		$highlighted &&
		css`
			background: ${theme.colors.brand.subtle};
		`}
`

const SelectAllRow = styled(OptionRow)`
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
	font-weight: ${Text.BOLD_600};
`

const OptionLabel = styled.span`
	flex: 1 1 0;
	min-width: 0;
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.primary};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	@media ${Devices.tablet} {
		font-size: ${Text.MEDIUM};
	}
`

const NoOptionsMessage = styled.div`
	padding: ${Spacings.SPACING_3B};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
	text-align: center;
`

const CreateItemButton = styled.button`
	width: 100%;
	border: none;
	background: ${({ theme }) => theme.colors.brand.primary};
	color: ${({ theme }) => theme.colors.text.inverse};
	font-weight: ${Text.BOLD_600};
	cursor: pointer;
	padding: ${Spacings.SPACING_2B};
	font-family: inherit;
	transition: background 0.2s ease;

	&:hover {
		background: ${({ theme }) => theme.colors.brand.hover};
	}
`

const ClearAllRow = styled.button`
	width: 100%;
	border: none;
	border-top: 1px solid ${({ theme }) => theme.colors.brand.subtle};
	background: ${({ theme }) => theme.colors.surface.card};
	color: ${({ theme }) => theme.colors.brand.primary};
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	padding: ${Spacings.SPACING_2B};
	cursor: pointer;
	font-family: inherit;

	&:hover {
		background: ${({ theme }) => theme.colors.brand.subtle};
	}
`

const isSameKey = (a, b) => String(a) === String(b)

const MultiSelect = ({
	id,
	label,
	data = [],
	value = [],
	displayKey,
	primaryKey,
	onChange,
	placeholder,
	disabled = false,
	readOnly = false,
	uppercase = false,
	createNewOption = false,
	hideSelectAll = false,
	error,
	small = false,
	noDropIcon = false,
	className,
	searchPlaceholder = 'Search...',
	clearAllLabel = 'Clear Filters',
	selectAllLabel = 'Select All',
}) => {
	const elemRef = useRef(null)
	const listRef = useRef(null)
	const filterFunctionRef = useRef(null)

	const [isOpen, setIsOpen] = useState(false)
	const [searchValue, setSearchValue] = useState('')
	const [options, setOptions] = useState(data)
	const [highlightedIndex, setHighlightedIndex] = useState(-1)

	const hasError = !!error
	const isSelected = (item) => value.some((v) => isSameKey(v[primaryKey], item[primaryKey]))

	useEffect(() => {
		filterFunctionRef.current = debounce(filterOptions, 200)
	}, [])

	useEffect(() => {
		setOptions(data)
	}, [data])

	useEffect(() => {
		const handleClickOutside = (e) => {
			const target = e.target
			if (target && elemRef.current && !elemRef.current.contains(target) && (!listRef.current || !listRef.current.contains(target))) {
				setIsOpen(false)
			}
		}

		if (isOpen) {
			window.addEventListener('mousedown', handleClickOutside)
		} else {
			window.removeEventListener('mousedown', handleClickOutside)
		}

		return () => window.removeEventListener('mousedown', handleClickOutside)
	}, [isOpen])

	useEffect(() => {
		if (isOpen) {
			setSearchValue('')
			setOptions(data)
			setHighlightedIndex(0)
			setTimeout(() => {
				listRef.current?.querySelector('input')?.focus()
			}, 0)
		} else {
			setHighlightedIndex(-1)
		}
	}, [isOpen])

	useEffect(() => {
		if (isOpen && filterFunctionRef.current) {
			filterFunctionRef.current(data, searchValue)
		}
	}, [searchValue])

	useEffect(() => {
		if (isOpen) {
			setHighlightedIndex(0)
			setTimeout(() => scrollHighlightedIntoView(0), 0)
		}
	}, [options])

	const filterOptions = (allData, query) => {
		if (query) {
			const normalized = query.toLowerCase().replaceAll(' ', '')
			setOptions((allData ?? []).filter((option) => option[displayKey]?.toLowerCase().replaceAll(' ', '').includes(normalized)))
		} else {
			setOptions(allData)
		}
	}

	const scrollHighlightedIntoView = (index) => {
		const item = listRef.current?.querySelector(`[data-highlight-index="${index}"]`)
		item?.scrollIntoView({ block: 'nearest' })
	}

	const toggleOption = (item) => {
		if (isSelected(item)) {
			onChange(value.filter((v) => !isSameKey(v[primaryKey], item[primaryKey])))
		} else {
			onChange([...value, item])
		}
	}

	const removeChip = (e, item) => {
		e.stopPropagation()
		onChange(value.filter((v) => !isSameKey(v[primaryKey], item[primaryKey])))
	}

	const handleSelectAllToggle = (e) => {
		e.stopPropagation()
		onChange(value.length === options.length ? [] : options)
	}

	const handleClearAll = (e) => {
		e.stopPropagation()
		onChange([])
	}

	const handleFieldClick = () => {
		if (disabled || readOnly) return
		setIsOpen((prev) => !prev)
	}

	const searchChangeHandler = (e) => {
		let val = e.target.value ?? ''
		if (uppercase) val = val.toUpperCase()
		setSearchValue(val)
	}

	const onCreateItemClickHandler = () => {
		if (!searchValue.trim()) return
		const optionValue = {
			primaryKey: primaryKey ?? null,
			displayKey: displayKey ?? null,
			[primaryKey]: searchValue,
			[displayKey]: searchValue,
			is_new_item: true,
		}
		onChange([...value, optionValue])
		setSearchValue('')
		setIsOpen(false)
	}

	const onSearchKeyDown = (e) => {
		if (e.keyCode === 40) {
			e.preventDefault()
			e.stopPropagation()
			setHighlightedIndex((prev) => {
				const next = Math.min(prev + 1, options.length - 1)
				scrollHighlightedIntoView(next)
				return next
			})
			return
		}
		if (e.keyCode === 38) {
			e.preventDefault()
			e.stopPropagation()
			setHighlightedIndex((prev) => {
				const next = Math.max(prev - 1, 0)
				scrollHighlightedIntoView(next)
				return next
			})
			return
		}
		if (e.keyCode === 13) {
			e.preventDefault()
			e.stopPropagation()
			if (options.length > 0 && highlightedIndex >= 0 && highlightedIndex < options.length) {
				toggleOption(options[highlightedIndex])
				return
			}
			if (createNewOption) {
				onCreateItemClickHandler()
			}
			return
		}
		if (e.keyCode === 27) {
			e.preventDefault()
			e.stopPropagation()
			setIsOpen(false)
		}
	}

	const onFieldKeyDown = (e) => {
		if (isOpen) return
		if (e.keyCode === 40 || e.keyCode === 38 || e.keyCode === 13) {
			e.preventDefault()
			setIsOpen(true)
		}
	}

	return (
		<MultiSelectContainer className={className}>
			{label && (
				<Label htmlFor={id} disabled={disabled}>
					{label}
				</Label>
			)}
			<FieldBox
				id={id}
				ref={elemRef}
				tabIndex={disabled ? -1 : 0}
				$disabled={disabled}
				$hasError={hasError}
				$small={small}
				$isOpen={isOpen}
				onClick={handleFieldClick}
				onKeyDown={onFieldKeyDown}
			>
				<ChipsRow>
					{value.length === 0 && <PlaceholderText>{placeholder}</PlaceholderText>}
					{value.map((item) => (
						<ChipItem key={`${id}-chip-${item[primaryKey]}`}>
							<ChipLabel>{item[displayKey]}</ChipLabel>
							{!disabled && !readOnly && (
								<ChipRemoveButton type='button' onClick={(e) => removeChip(e, item)}>
									<Close fontSize='inherit' />
								</ChipRemoveButton>
							)}
						</ChipItem>
					))}
				</ChipsRow>
				{!noDropIcon && !readOnly && !disabled && <IconWrapper>{isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconWrapper>}
			</FieldBox>
			<DropdownPanel ref={listRef} $show={isOpen}>
				<SearchRow>
					<SearchInput value={searchValue} onChange={searchChangeHandler} onKeyDown={onSearchKeyDown} placeholder={searchPlaceholder} />
				</SearchRow>
				{!hideSelectAll && options.length > 0 && (
					<SelectAllRow onClick={handleSelectAllToggle}>
						<Checkbox size='1' checked={value.length === options.length} onChange={() => {}} />
						<OptionLabel>{selectAllLabel}</OptionLabel>
					</SelectAllRow>
				)}
				<OptionsScrollArea>
					{options.length > 0 ? (
						options.map((item, index) => {
							const selected = isSelected(item)
							return (
								<OptionRow
									key={`${id}-option-${item[primaryKey] ?? index}`}
									data-highlight-index={index}
									$highlighted={index === highlightedIndex}
									onClick={() => toggleOption(item)}
								>
									<Checkbox size='1' checked={selected} onChange={() => {}} />
									<OptionLabel>{item[displayKey]}</OptionLabel>
								</OptionRow>
							)
						})
					) : (
						<>
							<NoOptionsMessage>No match found</NoOptionsMessage>
							{!!searchValue && createNewOption && (
								<CreateItemButton onClick={onCreateItemClickHandler}>Click here to select entered product</CreateItemButton>
							)}
						</>
					)}
				</OptionsScrollArea>
				<ClearAllRow onClick={handleClearAll}>{clearAllLabel}</ClearAllRow>
			</DropdownPanel>
			{error && <ErrorMessage>{error}</ErrorMessage>}
		</MultiSelectContainer>
	)
}

export default MultiSelect
